import { fetchOfferListFilters } from 'actions/OfferActions'
import { leSpecificHolidayTypes } from 'api/config/search'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyObject } from 'lib/object/objectUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { LOYALTY_STAY_UPGRADE_SEARCH_FILTER_VALUE } from 'loyalty/constants/loyaltySearchFilters'
import getLoyaltyStayUpgradeBenefitView from 'loyalty/selectors/getLoyaltyStayUpgradeBenefitView'
import { useEffect, useMemo } from 'react'

export const emptyFilters: App.OfferListFilterOptions = {
  error: undefined,
  fetching: true,
  filterOrder: {
    amenities: {},
    holidayTypes: {},
    propertyTypes: {},
  },
  filters: {
    amenities: [],
    bedrooms: [],
    campaigns: [],
    holidayTypes: [],
    offerTypes: [],
    propertyTypes: [],
    luxPlusFeatures: [],
    loyalty: [],
    agentHubFeatures: [],
    paymentOptions: [],
    chains: [],
    inclusions: [],
    customerRatings: [],
    total: 0,
  },
  orderedFilters: {
    amenities: [],
    holidayTypes: [],
  },
}

interface Options {
  disabled?: boolean

  /**
   * Whether to fetch the offer list or just return existing offer list
   */
  noFetch?: boolean
}

function useOfferListFilters(filters: App.OfferListFilters, options: Options = EmptyObject): App.OfferListFilterOptions {
  const key = getOfferListKey(filters)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!options.disabled && !options.noFetch && !filters?.isStream) {
      dispatch(fetchOfferListFilters(filters))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [key, options])

  const listFilterOptions = useAppSelector(state => state.offer.offerListFilterOptions[key]) ?? emptyFilters

  const hasLoyaltyStayUpgradeBenefit = !!useAppSelector(getLoyaltyStayUpgradeBenefitView)?.availability
  const isWhiteLabel = config.BRAND !== 'luxuryescapes'

  const listFilters = useMemo(() => {
    let finalListFilters = listFilterOptions

    if (!hasLoyaltyStayUpgradeBenefit && finalListFilters.filters) {
      finalListFilters = structuredClone(finalListFilters)
      finalListFilters.filters!.loyalty = finalListFilters.filters!.loyalty.filter(
        item => item.slug !== LOYALTY_STAY_UPGRADE_SEARCH_FILTER_VALUE,
      )
    }

    if (isWhiteLabel && finalListFilters.filters) {
      if (finalListFilters === listFilterOptions) {
        finalListFilters = structuredClone(finalListFilters)
      }
      finalListFilters.filters!.holidayTypes = finalListFilters.filters!.holidayTypes.map(item => {
        const mapping = leSpecificHolidayTypes.find(f => f.slug === item.slug)
        if (mapping) {
          return { ...item, label: mapping.whiteLabelLabel }
        }
        return item
      })
    }

    return finalListFilters
  }, [listFilterOptions, hasLoyaltyStayUpgradeBenefit, isWhiteLabel])

  return listFilters
}

export default useOfferListFilters
